import React from "react";
import { Backdrop, Slide, SlideProps, Snackbar } from "@mui/material";
import useTheme from "../../../common/hooks/useTheme";
import { useAppStore } from "../../../providers/AppStore";
import OutlineButton from "../../../common/ui/OutlineButton";
import PrimaryButton from "../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../common/ui/SecondaryButton";
import Success_icon from "../../../common/svgs/Success_icon";

export function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

// The exclamation mark shown by the alert snackbar, and by the two-action one
// unless its actions ask for the success tone.
const AlertMarkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="48"
    height="48"
    viewBox="0 0 61 60"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.875 3.75H36.125L34.25 37.5H26.75L24.875 3.75ZM36.125 50.625C36.125 53.7316 33.6066 56.25 30.5 56.25C27.3934 56.25 24.875 53.7316 24.875 50.625C24.875 47.5184 27.3934 45 30.5 45C33.6066 45 36.125 47.5184 36.125 50.625Z"
      fill="#3B3F42"
    />
  </svg>
);

/**
 * The global snackbar stack (alert / success / warning) driven by the
 * AppStore snackbar state. Rendered once per screen shell (Workspace and
 * Affiliate shells).
 */
const PlatformSnackbars = () => {
  const theme = useTheme();
  const {
    ASOpenSnackBar,
    ASSnackBarMessage,
    ASTypeSnackBar,
    ASHandleCloseSnackBar,
    ASSnackBarActions,
  } = useAppStore();

  return (
    <>
      {/* Custom backdrop that blocks clicks behind the alert snackbar */}
      {ASOpenSnackBar && ASTypeSnackBar === "alert" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9998,
            pointerEvents: "auto", // Blocks clicks on elements behind it
          }}
        />
      )}
      <Snackbar
        sx={{ zIndex: 9999 }}
        open={ASOpenSnackBar && ASTypeSnackBar === "alert"}
        autoHideDuration={5000}
        onClose={ASHandleCloseSnackBar}
        slots={{
          transition: SlideTransition,
        }}
        key="slide-transition-alert"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "450px",
            padding: "12px 16px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
          }}
        >
          <AlertMarkIcon />
          <p
            style={{
              fontSize: "14px",
              fontFamily: "var(--font-Bold)",
              color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
              textAlign: "center",
              marginBottom: "0px",
            }}
          >
            {ASSnackBarMessage.title}
          </p>
          <p
            style={{
              fontSize: "12px",
              fontFamily: "var(--font-Medium)",
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              textAlign: "center",
              whiteSpace: "pre-line",
            }}
          >
            {ASSnackBarMessage.message}
          </p>
          <OutlineButton
            text={"OK"}
            onClick={ASHandleCloseSnackBar}
            customWidth={"100%"}
          />
        </div>
      </Snackbar>
      <Snackbar
        sx={{ zIndex: 9999 }}
        open={ASOpenSnackBar && ASTypeSnackBar === "success"}
        autoHideDuration={2000}
        onClose={ASHandleCloseSnackBar}
        slots={{
          transition: SlideTransition,
        }}
        key="slide-transition-success"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "450px",
            marginTop: "48px",
            padding: "32px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            backgroundColor: theme.colorTokens.P[10],
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          }}
        >
          <p
            style={{
              fontSize: "16px",
              lineHeight: "28px",
              fontFamily: "var(--font-Medium)",
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              textAlign: "center",
            }}
          >
            {ASSnackBarMessage.message}
          </p>
        </div>
      </Snackbar>
      <Backdrop
        open={ASOpenSnackBar && ASTypeSnackBar === "warning"}
        style={{
          color: "#fff",
          zIndex: 9998, // Ensure it's above most components
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      <Snackbar
        sx={{ zIndex: 9999 }}
        open={ASOpenSnackBar && ASTypeSnackBar === "warning"}
        slots={{
          transition: SlideTransition,
        }}
        key="slide-transition-warning"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "450px",
            padding: "12px 16px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
          }}
        >
          {ASSnackBarActions.tone === "success" ? (
            <Success_icon size={48} />
          ) : (
            <AlertMarkIcon />
          )}
          <p
            style={{
              fontSize: "14px",
              fontFamily: "var(--font-Bold)",
              color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
              textAlign: "center",
              marginBottom: "0px",
            }}
          >
            {ASSnackBarMessage.title}
          </p>
          <p
            style={{
              fontSize: "12px",
              fontFamily: "var(--font-Medium)",
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              textAlign: "center",
            }}
          >
            {ASSnackBarMessage.message}
          </p>
          <div style={{ display: "flex", gap: "16px", width: "100%" }}>
            <SecondaryButton
              label={ASSnackBarActions.cancelLabel ?? "Cancel"}
              onClick={() => {
                ASSnackBarActions.onCancel?.();
                ASHandleCloseSnackBar({} as React.SyntheticEvent);
              }}
              sx={{ width: "100%", justifyContent: "center" }}
            />
            <PrimaryButton
              label={ASSnackBarActions.confirmLabel ?? "Confirm"}
              onClick={() => {
                ASSnackBarActions.onConfirm?.();
                ASHandleCloseSnackBar({} as React.SyntheticEvent);
              }}
              sx={{ width: "100%", justifyContent: "center" }}
            />
          </div>
        </div>
      </Snackbar>
    </>
  );
};

export default PlatformSnackbars;
